import { useMemo, useRef, type RefObject } from 'react'
import type { ToolCall } from '../../../../types'
import { resolvedPartsEqual } from '../comparators'
import { textPartKey, thinkingPartKey, toolPartKey } from '../parts/registry'
import { isTopLevelToolCall } from '../tools/tool-call-utils'
import { isAskUserToolCall } from '../../../../integrations/ask-user-inline'
import { joinContinuedText } from './join-continued-text'

export type GroupContentPart =
  // `sourceId` is the owning message's swap-stable identity (clientId ?? id,
  // with a positional fallback for identity-less messages) and `partIndex` the
  // part's index within that message — together the part's render key, per the
  // contract in parts/registry.
  | { type: 'text'; text: string; sourceId: string; partIndex: number }
  | { type: 'tool_call'; toolCallId: string }
  // `messageId` is the source assistant message — the key the thinking part
  // subscribes to for live reasoning text (`store/thinking-stream.ts`).
  | { type: 'thinking'; text: string; sourceId: string; messageId?: string }

export type ResolvedPart =
  | { type: 'text'; text: string; stableKey: string }
  | { type: 'tool_call'; toolCall: ToolCall; stableKey: string }
  | { type: 'thinking'; text: string; stableKey: string; messageId?: string }

type RenderPart = { type: 'text'; text: string; partIndex: number } | { type: 'tool_call'; toolCall: ToolCall }

const sameParts = (a: Array<ResolvedPart>, b: Array<ResolvedPart>): boolean =>
  a.length === b.length && a.every((part, index) => part === b[index])

export function useInterleavedParts(opts: {
  renderParts: Array<RenderPart>
  groupContentParts?: Array<GroupContentPart>
  groupToolCalls?: Array<ToolCall>
  allToolCallMapRef: RefObject<Map<string, ToolCall>>
  thinkingContent?: string
  messageId?: string
  /** The primary message's swap-stable identity — keys its own parts. */
  sourceId: string
}) {
  const { renderParts, groupContentParts, groupToolCalls, allToolCallMapRef, thinkingContent, messageId, sourceId } =
    opts

  // Use ref for thinking text so token updates don't trigger recomputation
  const thinkingContentRef = useRef(thinkingContent)
  thinkingContentRef.current = thinkingContent
  const hasThinking = !!thinkingContent?.trim()

  // Stable ordering — once a key is placed at a position, it never moves.
  const stableOrderRef = useRef<Array<string>>([])

  // Last emitted part per key. Parts are rebuilt from scratch on every run, so
  // without this every downstream memo would see all-new objects and no row
  // could ever bail out — which is why this hook's consumers used to need a
  // separate content fingerprint to tell them what actually changed.
  const prevPartsRef = useRef(new Map<string, ResolvedPart>())

  // The output arrays keep their identity too when every element does — this
  // is the ONE stability layer for parts, so consumers (`ToolCallList` and its
  // rows) memoize on plain references with no re-wrapping pass of their own.
  const prevResultRef = useRef<{
    interleavedParts: Array<ResolvedPart>
    trailingTextParts: Array<ResolvedPart & { type: 'text' }>
  } | null>(null)

  return useMemo(() => {
    const candidateMap = new Map<string, ResolvedPart>()
    const newKeysInOrder: Array<string> = []

    const addCandidate = (part: ResolvedPart) => {
      if (!candidateMap.has(part.stableKey)) {
        newKeysInOrder.push(part.stableKey)
      }
      candidateMap.set(part.stableKey, part)
    }

    if (groupContentParts && groupContentParts.length > 0) {
      for (const cp of groupContentParts) {
        if (cp.type === 'thinking' && cp.text?.trim()) {
          addCandidate({
            type: 'thinking',
            text: cp.text,
            messageId: cp.messageId,
            stableKey: thinkingPartKey(cp.sourceId),
          })
        } else if (cp.type === 'text' && cp.text?.trim()) {
          addCandidate({ type: 'text', text: cp.text, stableKey: textPartKey(cp.sourceId, cp.partIndex) })
        } else if (cp.type === 'tool_call') {
          const tc = allToolCallMapRef.current?.get(cp.toolCallId)
          if (tc && isTopLevelToolCall(tc)) {
            addCandidate({ type: 'tool_call', toolCall: tc, stableKey: toolPartKey(tc.id) })
          }
        }
      }
    }

    // Only add message-level thinking when groupContentParts didn't already contribute thinking entries
    const hasThinkingFromGroup = groupContentParts?.some((cp) => cp.type === 'thinking') ?? false
    if (!hasThinkingFromGroup && thinkingContentRef.current?.trim()) {
      addCandidate({
        type: 'thinking',
        text: thinkingContentRef.current,
        messageId,
        stableKey: thinkingPartKey(sourceId),
      })
    }

    for (const rp of renderParts) {
      if (rp.type === 'tool_call') {
        const stableTc = allToolCallMapRef.current?.get(rp.toolCall.id) ?? rp.toolCall
        addCandidate({ type: 'tool_call', toolCall: stableTc, stableKey: toolPartKey(rp.toolCall.id) })
      } else if (rp.type === 'text' && rp.text?.trim()) {
        // Keyed by the part's ORIGINAL index in its message's `contentParts`,
        // never its position in this array — `buildRenderParts` drops refs it
        // can't resolve (a subagent child tool call), so the two diverge. The
        // group path keys the same text by `partIndex`, so using it here is
        // what lets a message keep its keys when it demotes from primary to
        // submessage mid-turn.
        addCandidate({ type: 'text', text: rp.text, stableKey: textPartKey(sourceId, rp.partIndex) })
      }
    }

    if (groupToolCalls && groupToolCalls.length > 0) {
      for (const tc of groupToolCalls) {
        if (isTopLevelToolCall(tc)) {
          const stableTc = allToolCallMapRef.current?.get(tc.id) ?? tc
          addCandidate({ type: 'tool_call', toolCall: stableTc, stableKey: toolPartKey(tc.id) })
        }
      }
    }

    // Merge into stable order: keep existing positions, append only truly new keys
    const existingKeys = new Set(stableOrderRef.current)
    const appendKeys = newKeysInOrder.filter((k) => !existingKeys.has(k))
    stableOrderRef.current = [...stableOrderRef.current, ...appendKeys]
    stableOrderRef.current = stableOrderRef.current.filter((k) => candidateMap.has(k))

    const orderedParts: Array<ResolvedPart> = []
    for (const key of stableOrderRef.current) {
      const part = candidateMap.get(key)
      if (part) orderedParts.push(part)
    }

    // Reuse identities after joining, so a joined part is as stable as any other.
    const allParts = joinContinuedText(orderedParts).map((part) => {
      const prev = prevPartsRef.current.get(part.stableKey)
      return prev && resolvedPartsEqual(prev, part) ? prev : part
    })
    prevPartsRef.current = new Map(allParts.map((part) => [part.stableKey, part]))

    // Split off the final answer — the text rendered outside the tool list.
    // It is the primary message's own trailing text: its text parts after its
    // last tool call. Submessage text is interstitial by definition (a later
    // assistant message continued the turn), so it always stays in the group.
    // This applies during streaming too — the final text streams directly in
    // its destination position instead of jumping there when the stream ends.
    //
    // Derived from the primary's own parts rather than by scanning backwards
    // over the merged array: that scan promotes whatever text happens to sit
    // at the tail, so a submessage whose parts came back in a different order
    // — a history refetch rebuilds them from a flat content blob — had its
    // grouped text silently reclassified as the final answer.
    const finalAnswerKeys = new Set<string>()
    for (let i = renderParts.length - 1; i >= 0; i--) {
      const rp = renderParts[i]
      if (rp.type === 'tool_call') {
        // The turn ends on an ask-user call, so the text leading into it is the answer.
        if (isAskUserToolCall(rp.toolCall)) continue
        break
      }
      if (rp.text?.trim()) finalAnswerKeys.add(textPartKey(sourceId, rp.partIndex))
    }

    const next = {
      interleavedParts: allParts.filter((part) => !finalAnswerKeys.has(part.stableKey)),
      trailingTextParts: allParts.filter((part) => finalAnswerKeys.has(part.stableKey)) as Array<
        ResolvedPart & { type: 'text' }
      >,
    }
    const prev = prevResultRef.current
    if (
      prev &&
      sameParts(prev.interleavedParts, next.interleavedParts) &&
      sameParts(prev.trailingTextParts, next.trailingTextParts)
    ) {
      return prev
    }
    prevResultRef.current = next
    return next
  }, [renderParts, groupContentParts, groupToolCalls, hasThinking, messageId, sourceId])
}
